import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { useMembers } from "src/scenes/teams/members-provider";
import { Box, Chip } from "@mui/material";
import Typography from "@mui/material/Typography";
import fetchStatus from "../../../utils/api/fetch-status";

const TeamMemberAddModalAutocomplete = ({
    usersToAdd,
    setUsersToAdd,
    clearError,
    existingMembers,
}) => {
    const [inputValue, setInputValue] = React.useState("");
    const [members, setMembers] = React.useState([]);

    const orgMembersApi = useMembers();

    React.useEffect(() => {
        if (orgMembersApi.data) {
            const combinedMembers = [
                ...orgMembersApi.data.users.map((user) => ({
                    ...user,
                    isRegistered: true,
                })),
                ...(orgMembersApi.data.unregisteredUsers || []).map((user) => ({
                    ...user,
                    isRegistered: false,
                })),
            ].filter(
                (member) =>
                    !existingMembers.find((existingMember) => existingMember.id === member.id)
            );
            setMembers(combinedMembers);
        }
    }, [orgMembersApi.data, existingMembers]);

    const processInputValues = (input) => {
        const newUsers = input
            .split(/[,;\s]+/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0 && !/^\s*$/.test(email))
            .map((email) => ({ email, isRegistered: false })); // Add email and isRegistered
        setUsersToAdd((prev) => [...prev, ...newUsers]);
        setInputValue("");
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " " || event.key === ";" || event.key === ",") {
            event.preventDefault();
            processInputValues(inputValue);
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const pasteData = event.clipboardData.getData("text");
        processInputValues(pasteData);
    };

    return (
        <Stack sx={{ width: 450 }}>
            <Autocomplete
                value={usersToAdd}
                onChange={(event, newValue) => {
                    const updatedUsers = newValue.map((option) => {
                        if (typeof option === "string") {
                            return { email: option, isRegistered: false };
                        }
                        return option;
                    });
                    setUsersToAdd(updatedUsers);
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                loading={
                    orgMembersApi.status !== fetchStatus.SUCCESS &&
                    orgMembersApi.status !== fetchStatus.ERROR
                }
                multiple
                freeSolo
                id="tags-outlined"
                groupBy={(option) => (option.isRegistered ? "Registered" : "Unregistered")}
                options={members.sort((a, b) => {
                    if (a.isRegistered && b.isRegistered) {
                        if (a.hasSubscription !== b.hasSubscription) {
                            return a.hasSubscription ? -1 : 1; // Sort subscribed users first
                        }
                    }
                    return 0;
                })}
                getOptionLabel={(option) =>
                    option.hasSubscription
                        ? option.displayName || option.email || `${option} *`
                        : `${option.displayName || option.email}`
                }
                renderOption={(props, option) => (
                    <Box component="li" {...props}>
                        <Typography variant="body1">
                            {option.displayName || option.email}
                        </Typography>
                        {!option.isRegistered && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginLeft: "auto", fontSize: "11px" }}
                            >
                                (no user account)
                            </Typography>
                        )}
                        {option.isRegistered && !option.hasSubscription && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginLeft: "auto", fontSize: "11px" }}
                            >
                                (not in plan)
                            </Typography>
                        )}
                    </Box>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            label={`${option.displayName || option.email}${
                                !option.hasSubscription && option.isRegistered ? " *" : ""
                            }`}
                            {...getTagProps({ index })}
                        />
                    ))
                }
                filterSelectedOptions
                renderInput={(params) => (
                    <TextField
                        {...params}
                        onClick={() => clearError()}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        label="Users To Add"
                        placeholder="Start typing user name or email"
                        sx={{ minWidth: 450 }}
                    />
                )}
            />
        </Stack>
    );
};

export default TeamMemberAddModalAutocomplete;
